<!-- Страница управления элементами выбранного справочника. -->
<template>
  <div class="reference-items-container">
    <base-loader v-if="dataIsLoading" />

    <template v-else>
      <base-nav
        title="Справочники"
        :show-back="true"
        back-fallback="Home"
        :back-fallback-pattern="/\/reference_items/"
      >
        <base-primary-button
          class="nav-btn"
          @click="toggleCreateForm"
        >
          {{ showCreateForm ? "Отмена" : "Создать" }}
        </base-primary-button>
      </base-nav>

      <BaseTabs
        v-model="activeTab"
        :tabs="referenceItemTabs"
        class="reference-items-tabs"
        @tab-click="handleRegistryTabClick"
      />

      <BasePageHint
        v-if="activeTarget"
      >
        {{ activeTarget.description }}
      </BasePageHint>

      <!-- Форма создания -->
      <transition name="fade-slide">
        <div v-if="showCreateForm" class="card item-form-wrap">
          <h4 class="form-title">Создание: {{ registryTitle }}</h4>
          <reference-item-form
            :is-loading="isLoading"
            :existing-items="localItems"
            @save="handleCreate"
            @cancel="showCreateForm = false"
          />
        </div>
      </transition>

      <div v-if="items.length === 0" class="no-data">
        {{ registryTitle }} не найдены
      </div>

      <ul v-else class="reference-items-list">
        <BaseSettingsListItemCard
          v-for="item in localItems"
          :key="item.id"
          :enabled="item.enabled"
          :is-editing="editingItemId === item.id"
          :is-loading="isLoading"
          :edit-title="`Редактирование: ${registryTitle}`"
          :show-toggle="true"
          :show-delete="false"
          :created-at="item.created_at"
          :updated-at="item.updated_at"
          @edit="startEdit(item)"
          @toggle="toggleItem(item)"
        >
          <template #edit>
            <reference-item-form
              :initial-data="item"
              :is-loading="isLoading"
              :existing-items="localItems"
              @save="(payload) => handleUpdate(item.id, payload)"
              @cancel="editingItemId = null"
            />
          </template>

          <div class="item-top">
            <span
              class="item-color-dot"
              :style="{ backgroundColor: item.color || '#ccc' }"
            />
            <span class="item-name">{{ item.name || "—" }}</span>
            <span class="item-code">{{ item.code }}</span>
          </div>
        </BaseSettingsListItemCard>
      </ul>

      <pagination-component
        v-if="pagination.pages > 1 && !dataIsLoading"
        :page="pagination.page"
        :pages="pagination.pages"
        :series="pagination.series"
      />
    </template>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useReferenceItemsStore } from "@/stores/referenceItemsStore";
import { useNotificationStore } from "@/stores/notificationStore";

import PaginationComponent from "@/components/PaginationComponent.vue";
import ReferenceItemForm from "@/components/ReferenceItem/ReferenceItemForm.vue";

export default {
  name: "ReferenceItemsPage",
  components: {
    PaginationComponent,
    ReferenceItemForm,
  },
  data() {
    return {
      dataIsLoading: true,
      showCreateForm: false,
      editingItemId: null,
      localItems: [],
      activeTab: "",
    };
  },
  computed: {
    ...mapState(useReferenceItemsStore, [
      "items",
      "isLoading",
      "pagination",
      "targets",
    ]),
    notificationStore() {
      return useNotificationStore();
    },

    registryTitle() {
      return this.$route.query.title || "Типы заказа";
    },

    targetModel() {
      return this.$route.query.target_model || "Order";
    },

    targetField() {
      return this.$route.query.target_field || "order_type";
    },

    referenceItemTabs() {
      return this.targets.map((target) => ({
        key: `${target.target_model}:${target.target_field}`,
        label: target.title,
        icon: target.icon,
      }));
    },

    registryKey() {
      return `${this.targetModel}:${this.targetField}`;
    },

    activeTarget() {
      return this.targets.find(
        (target) =>
          `${target.target_model}:${target.target_field}` === this.registryKey
      );
    },
  },
  watch: {
    "$route.query.page": {
      immediate: false,
      handler(newPage) {
        this.loadItems(newPage);
      },
    },
    "$route.query.target_model": "handleRegistryChange",
    "$route.query.target_field": "handleRegistryChange",
  },
  methods: {
    ...mapActions(useReferenceItemsStore, [
      "getItemsRequest",
      "fetchTargets",
      "setTarget",
      "createItem",
      "updateItem",
      "itemEnable",
      "itemDisable",
    ]),

    async loadItems(page) {
      const pageNumber =
        parseInt(page) || parseInt(this.$route.query.page) || 1;
      try {
        this.dataIsLoading = true;
        await this.getItemsRequest({ page: pageNumber });
        this.localItems = [...this.items];
      } finally {
        this.dataIsLoading = false;
      }
    },

    async handleRegistryChange() {
      this.showCreateForm = false;
      this.editingItemId = null;
      this.setTarget(this.targetModel, this.targetField);
      this.activeTab = this.registryKey;
      await this.loadItems(1);
    },

    handleRegistryTabClick({ key }) {
      if (key === this.registryKey) return;

      const target = this.targets.find(
        (item) => `${item.target_model}:${item.target_field}` === key
      );
      if (!target) return;

      this.$router.replace({
        query: {
          target_model: target.target_model,
          target_field: target.target_field,
          title: target.title,
        },
      });
    },

    updateLocalItem(updatedItem) {
      const target = this.localItems.find(
        (i) => i.id === updatedItem.id
      );
      if (target) {
        Object.keys(target).forEach((key) => delete target[key]);
        Object.assign(target, updatedItem);
      }
    },

    toggleCreateForm() {
      this.showCreateForm = !this.showCreateForm;
      if (this.showCreateForm) {
        this.editingItemId = null;
      }
    },

    startEdit(item) {
      this.editingItemId = item.id;
      this.showCreateForm = false;
    },

    async handleCreate(payload) {
      try {
        await this.createItem(payload);
        this.localItems = [...this.items];
        this.showCreateForm = false;
        this.notificationStore.showSuccess(
          "Элемент справочника создан"
        );
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async handleUpdate(id, payload) {
      try {
        await this.updateItem(id, payload);
        this.localItems = [...this.items];
        this.editingItemId = null;
        this.notificationStore.showSuccess(
          "Элемент справочника обновлён"
        );
      } catch {
        // ошибка обрабатывается в сторе
      }
    },

    async toggleItem(item) {
      try {
        if (item.enabled) {
          await this.itemDisable(item.id);
          this.updateLocalItem({ ...item, enabled: false });
          this.notificationStore.showSuccess(
            "Элемент справочника отключён"
          );
        } else {
          await this.itemEnable(item.id);
          this.updateLocalItem({ ...item, enabled: true });
          this.notificationStore.showSuccess(
            "Элемент справочника включён"
          );
        }
      } catch {
        // ошибка обрабатывается в сторе
      }
    },
  },
  async created() {
    await this.fetchTargets();
    this.setTarget(this.targetModel, this.targetField);
    this.activeTab = this.registryKey;
    await this.loadItems(this.$route.query.page);
  },
};
</script>

<style scoped>
.reference-items-container {
  flex-grow: 1;
}

.item-form-wrap {
  margin-bottom: var(--base-out-indent);
}

.reference-items-tabs {
  margin-bottom: 12px;
}

.form-title {
  margin-bottom: var(--base-in-indent);
}

.reference-items-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 15px;
}

.item-top {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.item-color-dot {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
}

.item-name {
  font-weight: 600;
  font-size: 17px;
}

.item-code {
  color: #666;
}
</style>
